import {
  Button,
  ButtonProps,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import {
  SearchableMenuInput,
  SearchableMenuOption,
} from '@texas/components/shared/search/SearchableMenu';
import { Icons } from '../Icons';

type EntityDirectoryFilterValue = boolean | number | string;

export interface EntityDirectoryFilterOption<
  TValue extends EntityDirectoryFilterValue,
> {
  label: string;
  value: TValue;
}

interface EntityDirectorySelectFilterSearch {
  minimumLength?: number;
  maxResults?: number;
  noResultsMessage: string;
  placeholder: string;
  prompt: string;
}

interface EntityDirectorySelectFilterProps<
  TValue extends EntityDirectoryFilterValue,
> {
  buttonProps?: ButtonProps;
  options: EntityDirectoryFilterOption<TValue>[];
  placeholder: string;
  search?: EntityDirectorySelectFilterSearch;
  value?: TValue | null;
  onChange: (value: TValue | null) => void;
}

export function EntityDirectorySelectFilter<
  TValue extends EntityDirectoryFilterValue,
>({
  buttonProps,
  options,
  placeholder,
  search,
  value,
  onChange,
}: EntityDirectorySelectFilterProps<TValue>) {
  const [searchTerm, setSearchTerm] = useState('');
  const selectedOption = options.find((option) => option.value === value);
  const minimumSearchLength = search?.minimumLength ?? 2;
  const maxResults = search?.maxResults ?? 50;
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase();
  const filteredOptions = useMemo(
    () =>
      normalizedSearchTerm.length < minimumSearchLength
        ? []
        : options
            .filter((option) =>
              option.label.toLocaleLowerCase().includes(normalizedSearchTerm),
            )
            .slice(0, maxResults),
    [maxResults, minimumSearchLength, normalizedSearchTerm, options],
  );
  const displayedOptions = search ? filteredOptions : options;

  return (
    <Menu autoSelect={false} isLazy={true} onClose={() => setSearchTerm('')}>
      <MenuButton
        as={Button}
        h="38px"
        size="sm"
        variant="entityDirectoryFilter"
        {...buttonProps}
      >
        <Flex align="center" justify="space-between" minW={0} w="100%">
          <Flex flex={1} justify="flex-start" minW={0} textAlign="left">
            {selectedOption?.label ?? placeholder}
          </Flex>
          <Icons.ChevronDown boxSize={4} flexShrink={0} ml={2} />
        </Flex>
      </MenuButton>
      <Portal>
        <MenuList maxH="320px" minW="220px" overflowY="auto" zIndex={20}>
          {search && (
            <SearchableMenuInput
              autoFocus={true}
              placeholder={search.placeholder}
              value={searchTerm}
              onChange={setSearchTerm}
            />
          )}
          {(!search || value != null) && (
            <MenuItem
              fontWeight={value == null ? 'semibold' : 'normal'}
              onClick={() => onChange(null)}
            >
              {placeholder}
            </MenuItem>
          )}
          {search && normalizedSearchTerm.length < minimumSearchLength && (
            <Text
              color="entityDirectory.mutedText"
              fontSize="12px"
              px={3}
              py={3}
            >
              {search.prompt}
            </Text>
          )}
          {search &&
            normalizedSearchTerm.length >= minimumSearchLength &&
            displayedOptions.length === 0 && (
              <Text
                color="entityDirectory.mutedText"
                fontSize="12px"
                px={3}
                py={3}
              >
                {search.noResultsMessage}
              </Text>
            )}
          {/* Dynamic MenuItems make Chakra move focus away from the search input. */}
          {displayedOptions.map((option) => (
            <SearchableMenuOption
              key={String(option.value)}
              isSelected={value === option.value}
              label={option.label}
              onClick={() => onChange(option.value)}
            />
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );
}
